/* Das eigene Nutzerprofil: Anzeigename, Bild, Rolle.
 *
 * Der Zustand liegt im Modulbereich und wird geteilt (Singleton) — die
 * Kopfleiste, der Profil-Dialog und useAuth greifen auf dieselbe Zeile zu.
 *
 * WICHTIG: Diese Datei importiert bewusst NICHT useAuth. useAuth liest von
 * hier die Rolle; ein Import in die Gegenrichtung waere ein Ringschluss. Die
 * Nutzerkennung kommt deshalb immer als Parameter herein.
 */

import { ref, computed } from 'vue';
import { loadOwnProfile, saveOwnProfile } from '../supabase/profiles.js';

const profile = ref(null);
/** null = noch nicht geprueft · true = Tabelle vorhanden · false = fehlt noch */
const tableAvailable = ref(null);
const loading = ref(false);
let loadedFor = null;

export function useProfile() {
  /** Anzeigename aus dem Profil — leer, solange keins geladen ist. */
  const profileName = computed(() => profile.value?.full_name || '');
  const profileAvatarPath = computed(() => profile.value?.avatar_path || '');
  const profileRole = computed(() => profile.value?.role || null);

  /**
   * Profil des angemeldeten Nutzers holen. Mehrfachaufrufe fuer denselben
   * Nutzer sind billig — geladen wird nur beim ersten Mal oder nach einem
   * Nutzerwechsel.
   */
  async function loadProfile(userId, { force = false } = {}) {
    if (!userId) {
      resetProfile();
      return null;
    }
    if (!force && loadedFor === userId) return profile.value;

    loading.value = true;
    try {
      profile.value = await loadOwnProfile(userId);
      tableAvailable.value = true;
      loadedFor = userId;
    } catch (e) {
      /* Fehlt die Tabelle, laeuft die App im bisherigen Zustand weiter:
         Rolle aus den Nutzermetadaten, Name aus der Anmeldung. */
      if (e.message === 'profilesMissing') {
        tableAvailable.value = false;
        profile.value = null;
        loadedFor = userId;
      } else {
        profile.value = null;
      }
    } finally {
      loading.value = false;
    }
    return profile.value;
  }

  /** Eigenen Namen und/oder das eigene Bild speichern. */
  async function saveProfile(userId, patch) {
    const updated = await saveOwnProfile(userId, patch);
    if (updated) profile.value = updated;
    return updated;
  }

  /** Nach dem Abmelden: sonst sieht der naechste Nutzer den alten Namen. */
  function resetProfile() {
    profile.value = null;
    loadedFor = null;
    /* tableAvailable bleibt stehen — ob die Tabelle existiert, haengt nicht
       am angemeldeten Nutzer. */
  }

  return {
    profile, loading, tableAvailable,
    profileName, profileAvatarPath, profileRole,
    loadProfile, saveProfile, resetProfile,
  };
}
